/**
 * Page-aware context for AI tools.
 * Determines which AI agents/actions are relevant on the current route
 * and prevents stale transcript references from leaking across pages.
 */

export type PageType =
  | 'transcript'
  | 'video'
  | 'dashboard'
  | 'orders'
  | 'team'
  | 'assets'
  | 'profile'
  | 'corrector'
  | 'admin'
  | 'public'
  | 'unknown';

export type AiAgentId = 'transcript' | 'global' | 'page-help';

export interface PageContext {
  path: string;
  name: string;
  pageType: PageType;
  /** Human label for UI */
  label: string;
  /** Whether a transcript element can be meaningfully resolved on this page */
  supportsTranscriptContext: boolean;
  /** Which agents make sense here */
  availableAgents: AiAgentId[];
}

const PAGE_TYPE_LABEL: Record<PageType, string> = {
  transcript: 'Transcript',
  video: 'Video',
  dashboard: 'Dashboard',
  orders: 'Orders',
  team: 'Team',
  assets: 'Assets',
  profile: 'Profile',
  corrector: 'Corrector',
  admin: 'Admin',
  public: 'Public',
  unknown: 'Page',
};

export function getPageType(path: string, routeName?: string): PageType {
  const p = (path || '').toLowerCase();
  const n = (routeName || '').toLowerCase();
  if (p.includes('/users/transcriptordashboard') || n === 'transcripteditordashboard') return 'transcript';
  if (p.includes('/users/videodashboard') || n === 'videogeneratordashboard') return 'video';
  if (p.includes('/users/dashboard') || n === 'userdashboard' || n === 'maindashboard') return 'dashboard';
  if (p.includes('/users/orders')) return 'orders';
  if (p.includes('/users/team')) return 'team';
  if (p.includes('/users/assets')) return 'assets';
  if (p.includes('/users/profile')) return 'profile';
  if (p.includes('/correctors')) return 'corrector';
  if (p.includes('/superadmins') || p.includes('/admins')) return 'admin';
  if (p.startsWith('/home') || p === '/' || p.includes('/about') || p.includes('/help') || p.includes('/login') || p.includes('/terms') || p.includes('/privacy') || p.includes('/data-handling') || p.includes('/api-docs')) return 'public';
  return 'unknown';
}

export function getPageContext(path: string, routeName?: string): PageContext {
  const pageType = getPageType(path, routeName);
  const label = PAGE_TYPE_LABEL[pageType] || 'Page';
  const supportsTranscriptContext = pageType === 'transcript' || pageType === 'video';
  // Dashboard supports transcript context only if a file is explicitly selected,
  // but we treat it as not supporting by default to avoid stale references.
  const availableAgents: AiAgentId[] = [];
  if (supportsTranscriptContext) {
    availableAgents.push('transcript', 'global');
  } else if (pageType === 'dashboard') {
    availableAgents.push('global');
  } else if (pageType === 'public' || pageType === 'unknown') {
    availableAgents.push('global', 'page-help');
  } else {
    availableAgents.push('global', 'page-help');
  }
  return {
    path,
    name: routeName || '',
    pageType,
    label,
    supportsTranscriptContext,
    availableAgents,
  };
}

/**
 * Whether the current route is considered a transcript context page.
 * Used to gate resolveCurrentElement() so stale dashboard selections
 * don't leak onto unrelated pages (Orders, Team, etc.).
 */
export function isTranscriptContextRoute(path: string, routeName?: string): boolean {
  const ctx = getPageContext(path, routeName);
  return ctx.supportsTranscriptContext;
}
